import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiPost } from "../../lib/api";
import { QrCodeImage } from "../../components/QrCodeImage";
import {
  isSignupPortalUiLocale,
} from "./signupPortal.copy";

type Method = "telegram" | "email" | "sms";

const COUNTRIES = ["US", "NL", "BR", "DE", "FR", "ES", "GB", "UA"];
const LOCALES = ["en", "es", "ru", "de", "fa", "pt", "nl", "fr"];

export function SignupPortalPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const browserLocale = useMemo(() => (navigator.language || "en").slice(0, 2).toLowerCase(), []);
  const [method, setMethod] = useState<Method>("telegram");
  const [country, setCountry] = useState("US");
  const [locale, setLocale] = useState(LOCALES.includes(browserLocale) ? browserLocale : "en");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const uiLocale = isSignupPortalUiLocale(locale) ? locale : "en";
  const tr = (key: string, fallback: string, options?: Record<string, unknown>) => {
    const translated = t(key, { lng: uiLocale, defaultValue: fallback, ...options });
    return translated === key ? fallback : String(translated);
  };

  const onboardingQrValue = useMemo(() => `${window.location.origin}/signup`, []);

  const startTelegramSignup = async () => {
    setBusy(true);
    setError("");
    setInfo("");
    try {
      const res = await apiPost<{ signup_attempt_id: string; next: { type: string; value: string } }>(
        "/v3/public/signup/start",
        {
          method: "telegram",
          selected_country_code: country,
          locale,
          captcha_token: captchaToken || null,
          referrer: window.location.href,
        },
        { auth: "none" }
      );
      if (res.next?.value) {
        window.location.href = res.next.value;
        return;
      }
      setError(tr("signupPortal.error.noTelegramLink", "No Telegram link returned"));
    } catch (e) {
      setError(e instanceof Error ? e.message : tr("signupPortal.error.failedStartSignup", "Failed to start signup"));
    } finally {
      setBusy(false);
    }
  };

  const sendFallback = async () => {
    setBusy(true);
    setError("");
    setInfo("");
    try {
      if (method === "email") {
        const res = await apiPost<{ verification_id: string; dev_code?: string | null }>(
          "/v3/public/signup/email/send-magic-link",
          { email, selected_country_code: country, locale, captcha_token: captchaToken || null },
          { auth: "none" }
        );
        setVerificationId(res.verification_id);
        if (res.dev_code) {
          setVerificationCode(res.dev_code);
          setInfo(tr("signupPortal.info.devVerificationCode", "Dev verification code: {{code}}", { code: res.dev_code }));
        } else {
          setInfo(tr("signupPortal.info.magicLinkSent", "Magic link sent."));
        }
      } else {
        const res = await apiPost<{ verification_id: string; dev_code?: string | null }>(
          "/v3/public/signup/sms/send-otp",
          { phone, selected_country_code: country, locale, captcha_token: captchaToken || null },
          { auth: "none" }
        );
        setVerificationId(res.verification_id);
        if (res.dev_code) {
          setVerificationCode(res.dev_code);
          setInfo(tr("signupPortal.info.devOtpCode", "Dev OTP code: {{code}}", { code: res.dev_code }));
        } else {
          setInfo(tr("signupPortal.info.otpSent", "OTP sent."));
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : tr("signupPortal.error.failedSendVerification", "Failed to send verification"));
    } finally {
      setBusy(false);
    }
  };

  const verifyFallback = async () => {
    if (!verificationId || !verificationCode) return;
    setBusy(true);
    setError("");
    try {
      const path = method === "email" ? "/v3/public/signup/email/verify" : "/v3/public/signup/sms/verify";
      const res = await apiPost<{ signup_session_jwt: string; preset: Record<string, unknown>; verification_method: string }>(
        path,
        { verification_id: verificationId, code: verificationCode },
        { auth: "none" }
      );
      sessionStorage.setItem(
        "pending_signup_session",
        JSON.stringify({ signup_session_jwt: res.signup_session_jwt, preset: res.preset, verification_method: res.verification_method })
      );
      navigate("/tg/onboarding?mode=fallback");
    } catch (e) {
      setError(e instanceof Error ? e.message : tr("signupPortal.error.verificationFailed", "Verification failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-4 md:p-8" lang={uiLocale} dir={uiLocale === "fa" ? "rtl" : "ltr"}>
      <div className="mx-auto max-w-3xl panel p-6 md:p-8 grid gap-5">
        <div>
          <p className="text-xs uppercase tracking-[0.16em] text-slate-500">{tr("signupPortal.overline", "Salon OS")}</p>
          <h1 className="text-2xl md:text-3xl font-semibold mt-2">{tr("signupPortal.title", "Start your salon in minutes")}</h1>
          <p className="text-sm text-slate-600 mt-2">{tr("signupPortal.subtitle", "Self-service onboarding with Telegram-first flow, plus Email/SMS fallback.")}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {(["telegram", "email", "sms"] as Method[]).map((m) => (
            <button
              key={m}
              type="button"
              className={method === m ? "btn" : "btn secondary"}
              onClick={() => {
                setMethod(m);
                setError("");
                setInfo("");
              }}
            >
              {m === "telegram"
                ? tr("signupPortal.method.telegram", "Telegram")
                : m === "email"
                  ? tr("signupPortal.method.email", "Email")
                  : tr("signupPortal.method.sms", "SMS")}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <label className="grid gap-1 text-sm">
            <span>{tr("signupPortal.field.country", "Country")}</span>
            <select className="input" value={country} onChange={(e) => setCountry(e.target.value)}>
              {COUNTRIES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-sm">
            <span>{tr("signupPortal.field.language", "Language")}</span>
            <select className="input" value={locale} onChange={(e) => setLocale(e.target.value)}>
              {LOCALES.map((code) => (
                <option key={code} value={code}>
                  {tr(`signupPortal.locale.${code}`, code.toUpperCase())}
                </option>
              ))}
            </select>
          </label>
        </div>

        {method === "email" ? (
          <input className="input" placeholder={tr("signupPortal.placeholder.email", "owner@example.com")} value={email} onChange={(e) => setEmail(e.target.value)} />
        ) : null}
        {method === "sms" ? (
          <input className="input" placeholder={tr("signupPortal.placeholder.phone", "+1 (555) 000-0000")} value={phone} onChange={(e) => setPhone(e.target.value)} />
        ) : null}

        <input className="input" placeholder={tr("signupPortal.placeholder.captcha", "CAPTCHA token (optional unless required)")} value={captchaToken} onChange={(e) => setCaptchaToken(e.target.value)} />

        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="mt-1" />
          <span>{tr("signupPortal.terms.accept", "I accept the Terms and Privacy Policy.")}</span>
        </label>

        {method === "telegram" ? (
          <button className="btn" disabled={!termsAccepted || busy} onClick={startTelegramSignup}>
            {busy ? tr("signupPortal.action.starting", "Starting...") : tr("signupPortal.action.continueTelegram", "Continue with Telegram")}
          </button>
        ) : (
          <div className="grid gap-2">
            <button
              className="btn"
              disabled={!termsAccepted || busy || (method === "email" ? !email.trim() : !phone.trim())}
              onClick={sendFallback}
            >
              {busy
                ? tr("signupPortal.action.sending", "Sending...")
                : method === "email"
                  ? tr("signupPortal.action.sendMagicLink", "Send Magic Link")
                  : tr("signupPortal.action.sendOtp", "Send OTP")}
            </button>
            {verificationId ? (
              <div className="grid gap-2 rounded border border-slate-200 bg-white p-3">
                <p className="text-xs text-slate-500">
                  {tr("signupPortal.verification.id", "Verification ID")}: <code>{verificationId}</code>
                </p>
                <input
                  className="input"
                  placeholder={tr("signupPortal.verification.codePlaceholder", "Verification code")}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                />
                <button className="btn secondary" disabled={busy || !verificationCode.trim()} onClick={verifyFallback}>
                  {busy
                    ? tr("signupPortal.action.verifying", "Verifying...")
                    : tr("signupPortal.action.verifyAndContinue", "Verify and Continue Onboarding")}
                </button>
              </div>
            ) : null}
          </div>
        )}

        <div className="rounded border border-slate-200 bg-slate-50 p-3 grid md:grid-cols-[1fr_auto] gap-3 items-center">
          <div>
            <p className="text-sm font-medium">{tr("signupPortal.qr.title", "QR Onboarding Entry")}</p>
            <p className="text-xs text-slate-600 mt-1">{tr("signupPortal.qr.body", "Use this QR code in in-person campaigns to open the same `/signup` flow.")}</p>
          </div>
          <QrCodeImage value={onboardingQrValue} alt={tr("signupPortal.qr.alt", "Signup QR")} size={180} className="w-28 h-28 rounded border border-slate-200 bg-white p-1" />
        </div>

        <p className="text-xs text-slate-500">
          {tr(
            "signupPortal.dev.note",
            "In dev/local, Email/SMS verification endpoints return a `dev_code` so you can complete the flow without external providers."
          )}
        </p>
        {info ? <p className="text-sm text-emerald-700">{info}</p> : null}
        {error ? <p className="text-sm text-rose-600">{error}</p> : null}
      </div>
    </div>
  );
}
